import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  useTheme,
  Divider,
  Grid,
  Paper,
  Button,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  FormGroup,
  CircularProgress,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Typography,
  Avatar,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { tokens } from "../theme";
import { useState, useEffect } from "react";
import Header from "../components/Header";
import BreadcrumbsBar from "../components/BreadcrumbsBar";
import {
  Cancel,
  ChangeCircle,
  Check,
  CheckBox,
  Delete,
  Edit,
  GridView,
  HolidayVillage,
  ListAlt,
  LocationCity,
  ViewList,
  ViewListOutlined,
} from "@mui/icons-material";
import axios from "axios";
import { useUser } from "../provider/AuthProvider";
import AddUnitsWizard from "../components/AddPropertyDialog";
import AddModeDialog from "../components/AddModeDialog";
import AddSingleUnitDialog from "../components/AddSingleUnitDialog";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../components/ComfirmationDialog";

const STATUS_OPTIONS = ["VACANT", "RENTED", "OWNER_OCCUPIED"];
const TYPE_OPTIONS = ["Tower", "Villa"];
const renderFilterGroup = (
  label,
  options,
  selectedValues,
  setSelectedValues,
  getLabel
) => {
  const allSelected =
    options.length > 0 && selectedValues.length === options.length;
  const partiallySelected = selectedValues.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedValues(allSelected ? [] : [...options]);
  };

  const toggleOne = (option) => {
    setSelectedValues((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option]
    );
  };

  return (
    <Box mt={2}>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={partiallySelected}
              onChange={toggleAll}
            />
          }
          label={label}
        />
        <Box sx={{ display: "flex", flexDirection: "column", ml: 3 }}>
          {options.map((option) => (
            <FormControlLabel
              key={option}
              control={
                <Checkbox
                  checked={selectedValues.includes(option)}
                  onChange={() => toggleOne(option)}
                />
              }
              label={getLabel ? getLabel(option) : option}
            />
          ))}
        </Box>
      </FormGroup>
    </Box>
  );
};

const PropertyManagementPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useUser();
  const [statusFilter, setStatusFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [buildingFilter, setBuildingFilter] = useState("");
  const [unitSearch, setUnitSearch] = useState("");

  const [filteredUnits, setFilteredUnits] = useState([]);
  const [addPropertyDlgOpen, setAddPropertyDlgOpen] = useState(false);

  const [modeDialogOpen, setModeDialogOpen] = useState(false);
  const [singleDialogOpen, setSingleDialogOpen] = useState(false);

  const [structures, setStructures] = useState(["A"]);
  const buildingType = "tower"; // or "villa"
  const [viewType, setViewType] = useState("card"); // "card" or "table"

  const [buildings, setBuildings] = useState([]);

  const [units, setUnits] = useState([]);

  const [loading, setLoading] = useState(false);

  const [editingUnitId, setEditingUnitId] = useState(null);
  const [editingUnitName, setEditingUnitName] = useState("");
  const [savingUnit, setSavingUnit] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentUnit, setCurrentUnit] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate();

  const handleShowTenant = (tenant) => {
    // Option 1: use query param
    navigate(`/tenant?focusId=${tenant.id}`);
    // Option 2: use navigation state (more hidden from URL)
    // navigate('/tenants', { state: { focusId: tenant.id } });
  };
  useEffect(() => {
    fetchInitData();
  }, []);

  const handleEditUnitName = (unit) => {
    setEditingUnitId(unit.id);
    setEditingUnitName(unit.name || "");
  };

  const handleDeleteUnit = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/compound/unit/${currentUnit.id}/deleteUnit`
      );
      setUnits((prev) =>
        prev.map((b) =>
          b.building_id === currentUnit.building_id // <- FIXED typo here!
            ? {
                ...b,
                units: b.units.filter((u) => u.id !== currentUnit.id),
              }
            : b
        )
      );
      setSnackbar({
        open: true,
        message: t("properties.unit_deleted"),
        severity: "success",
      });
      setCurrentUnit(null);
      setDeleteDialogOpen(false);
    } catch (error) {
      setSnackbar({ open: true, message: t("properties.delete_failed"), severity: "error" });
    } finally {
      setIsDeleting(false);
    }
  };
  const handleUnitNameChange = (e) => {
    setEditingUnitName(e.target.value);
  };

  const handleUnitNameSave = async (unit) => {
    console.log("Unit to update:", unit);

    if (!editingUnitName.trim() || editingUnitName === unit.name) {
      setEditingUnitId(null);
      return;
    }
    setSavingUnit(true);

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/compound/unit/${unit.id}/updateName`,
        {
          name: editingUnitName,
        }
      );
      // Update only that unit locally!
      setUnits((prev) =>
        prev.map((b) =>
          b.building_id === unit.building_id // <- FIXED typo here!
            ? {
                ...b,
                units: b.units.map((u) =>
                  u.id === unit.id ? { ...u, name: editingUnitName } : u
                ),
              }
            : b
        )
      );
      setSnackbar({
        open: true,
        message: t("properties.unit_name_updated"),
        severity: "success",
      });
    } catch (error) {
      setSnackbar({ open: true, message: t("properties.update_failed"), severity: "error" });
    } finally {
      setEditingUnitId(null);
      setSavingUnit(false);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [statusFilter, typeFilter, buildingFilter, units, unitSearch]);

  const applyFilters = () => {
    let filtered = [...units];

    if (statusFilter.length > 0) {
      filtered = filtered.map((b) => ({
        ...b,
        units: b.units.filter((unit) => statusFilter.includes(unit.status)),
      }));
    }

    if (typeFilter.length > 0) {
      filtered = filtered.filter((b) => typeFilter.includes(b.type));
    }

    if (buildingFilter) {
      filtered = filtered.filter((b) =>
        b.building_name.toLowerCase().includes(buildingFilter.toLowerCase())
      );
    }
    if (unitSearch) {
      filtered = filtered
        .map((b) => ({
          ...b,
          units: b.units.filter((unit) => {
            const nameMatch = unit.name
              ?.toLowerCase()
              .includes(unitSearch.toLowerCase());
            const flatMatch = unit.flat_number
              ?.toLowerCase()
              .includes(unitSearch.toLowerCase());
            return nameMatch || flatMatch;
          }),
        }))
        .filter((b) => b.units.length > 0); // keep only buildings with matching units
    }

    // Remove buildings with no units after filtering
    filtered = filtered.filter((b) => b.units.length > 0);

    setFilteredUnits(filtered);
  };

  const handleAddMode = (mode) => {
    setModeDialogOpen(false);
    if (mode === "bulk") setAddPropertyDlgOpen(true);
    else if (mode === "single") setSingleDialogOpen(true);
  };

  const handleSingleAdd = (unit) => {
    fetchInitData();
    // setUnits((prev) => [...prev, unit]);

    // // auto-add structure if new
    // const structureName = buildingType === "Tower" ? unit.tower : unit.area;
    // if (!structures.includes(structureName)) {
    //   setStructures((prev) => [...prev, structureName]);
    // }
  };

  const getBuildingIcon = (type) => {
    if (type === "Tower") return <LocationCity />;
    if (type === "Villa") return <HolidayVillage />;
    return null;
  };

  const renderStatusChip = (status) => {
    const color = status === "VACANT" ? "success" : "warning";
    const label =
      status === "VACANT"
        ? t("properties.status_vacant")
        : status === "RENTED"
        ? t("properties.status_rented")
        : t("properties.status_owner_occupied");
    return <Chip label={label} color={color} size="small" />;
  };

  const fetchInitData = async () => {
    try {
      setLoading(true);
      const [reqRes, bldgRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/compound/units/${user.id}`
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/cmt/buildings/${user.id}`
        ),
      ]);
      setUnits(reqRes.data);
      console.log(reqRes.data);
      setBuildings(bldgRes.data);
    } catch (err) {
      console.error("Error fetching data", err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box className="main-container mt-4">
      <BreadcrumbsBar />
      <Grid container spacing={3} columns={{ xs: 2, sm: 8, md: 10 }}>
        <Grid item size={{ xs: 2, sm: 4, md: 3 }}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: "80vh" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setModeDialogOpen(true)}
            >
              {t("properties.add")}
            </Button>
            <FormControl
              component="fieldset"
              sx={{
                border: "1px solid #ccc",
                borderRadius: 1,
                display: "flex",
                mt: 3,
                p: 1, // padding inside border
              }}
            >
              <FormLabel component="legend">{t("properties.search")}</FormLabel>
              <TextField
                label={t("properties.search_by_name")}
                variant="outlined"
                size="small"
                value={unitSearch}
                onChange={(e) => setUnitSearch(e.target.value)}
              />
              {renderFilterGroup(
                t("properties.status"),
                STATUS_OPTIONS,
                statusFilter,
                setStatusFilter,
                (opt) => t(`properties.status_${opt.toLowerCase()}`)
              )}
              {renderFilterGroup(
                t("properties.type"),
                TYPE_OPTIONS,
                typeFilter,
                setTypeFilter,
                (opt) => t(`properties.type_${opt.toLowerCase()}`)
              )}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>{t("properties.building")}</InputLabel>
                <Select
                  className="notranslate"
                  value={buildingFilter}
                  onChange={(e) => setBuildingFilter(e.target.value)}
                  label={t("properties.building")}
                >
                  <MenuItem value="">{t("properties.all")}</MenuItem>
                  {buildings.map((b) => (
                    <MenuItem key={b.id} value={b.name} className="notranslate">
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setStatusFilter([]);
                  setTypeFilter([]);
                  setBuildingFilter("");
                  setUnitSearch("");
                }}
                sx={{ mt: 2 }}
              >
                {t("properties.clear_filters")}
              </Button>
            </FormControl>
          </Paper>
        </Grid>
        <Grid item size={{ xs: 2, sm: 4, md: 7 }}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: "80vh" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              {/* <Tooltip title="CardView">
                <Button
                  variant={viewType === "card" ? "contained" : "outlined"}
                  startIcon={<GridView />}
                  onClick={() => setViewType("card")}
                  sx={{ mr: 1 }}
                />
              </Tooltip>
              <Tooltip title="TableView">
                <Button
                  variant={viewType === "table" ? "contained" : "outlined"}
                  startIcon={<ViewList />}
                  onClick={() => setViewType("table")}
                />
              </Tooltip> */}
              <Tooltip title={t("properties.card_view")}>
                <IconButton
                  color={viewType === "card" ? "primary" : "default"}
                  onClick={() => setViewType("card")}
                  sx={{
                    border: viewType === "card" ? "2px solid" : "none",
                    borderColor: "primary.main",
                    mr: 1,
                  }}
                >
                  <GridView />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("properties.table_view")}>
                <IconButton
                  color={viewType === "table" ? "primary" : "default"}
                  onClick={() => setViewType("table")}
                  sx={{
                    border: viewType === "table" ? "2px solid" : "none",
                    borderColor: "primary.main",
                  }}
                >
                  <ViewList />
                </IconButton>
              </Tooltip>
            </Box>

            <Box>
              {loading == true ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ height: "80vh" }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ overflow: "auto", height: "70vh" }}>
                  {filteredUnits.length === 0 ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ height: "60vh", textAlign: "center" }}
                    >
                      <Typography variant="h6" gutterBottom>
                        {t("properties.no_units_found")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("properties.adjust_filters")}
                      </Typography>
                    </Box>
                  ) : viewType === "card" ? (
                    filteredUnits.map((building) => (
                      <Box key={building.building_id} sx={{ mb: 4, p: 2 }}>
                        {/* Building Header */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                            {getBuildingIcon(building.type)}
                          </Avatar>
                          <Box className="notranslate">
                            <Typography variant="h6">
                              {building.building_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {building.location}
                            </Typography>
                          </Box>
                        </Box>

                        <Grid
                          container
                          spacing={3}
                          alignItems="stretch"
                          sx={{ width: "100%", m: 0 }}
                        >
                          {building.units.map((unit) => (
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              md={4}
                              lg={3}
                              key={unit.id}
                              sx={{ display: "flex" }}
                            >
                              <Card
                                variant="outlined"
                                sx={{
                                  width: "100%",
                                  height: 250,
                                  display: "flex",
                                  flexDirection: "column",
                                  borderRadius: 3,
                                  bgcolor: theme.palette.background.paper,
                                  "&:hover": {
                                    borderColor: theme.palette.primary.main,
                                  },
                                }}
                              >
                                <CardContent
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    p: 2,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                    position: "relative",
                                  }}
                                >
                                  <Box textAlign="right" mb={1}>
                                    <Chip
                                      size="small"
                                      label={
                                        unit.status === "VACANT"
                                          ? t("properties.status_vacant")
                                          : unit.status === "RENTED"
                                          ? t("properties.status_rented")
                                          : t("properties.status_owner_occupied")
                                      }
                                      sx={{
                                        bgcolor:
                                          unit.status === "VACANT"
                                            ? "#61b05e"
                                            : unit.status === "RENTED"
                                            ? "#ffa726"
                                            : "#2b7ce6",
                                        color: "#fff",
                                        borderRadius: 20,
                                      }}
                                    />
                                  </Box>

                                  {editingUnitId === unit.id ? (
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      sx={{
                                        justifyContent: "center",
                                        gap: 1,
                                        mb: 1,
                                      }}
                                    >
                                      <TextField
                                        size="small"
                                        value={editingUnitName}
                                        onChange={handleUnitNameChange}
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter")
                                            handleUnitNameSave(unit);
                                          if (e.key === "Escape")
                                            setEditingUnitId(null);
                                        }}
                                        disabled={savingUnit}
                                        sx={{ maxWidth: 140 }}
                                        className="notranslate"
                                      />
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        disabled={savingUnit}
                                        onClick={() => handleUnitNameSave(unit)}
                                      >
                                        <CheckBox />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        disabled={savingUnit}
                                        onClick={() => setEditingUnitId(null)}
                                      >
                                        <Cancel />
                                      </IconButton>
                                    </Box>
                                  ) : (
                                    <Tooltip title={t("properties.click_to_edit")}>
                                      <Typography
                                        variant="h6"
                                        fontWeight={700}
                                        sx={{
                                          textAlign: "center",
                                          mb: 1,
                                          mt: 1,
                                          fontSize: "1rem",
                                          width: "100%",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          cursor: "pointer",
                                          "&:hover": {
                                            textDecoration: "underline",
                                            color: theme.palette.primary.main,
                                          },
                                        }}
                                        onClick={() => handleEditUnitName(unit)}
                                        className="notranslate"
                                      >
                                        {unit.name ||
                                          `Flat ${unit.flat_number} ${building.building_name}`}
                                      </Typography>
                                    </Tooltip>
                                  )}

                                  <Divider
                                    sx={{
                                      mb: 1,
                                    }}
                                  />
                                  <Box
                                    sx={{ display: "flex", gap: 1.4, mb: 0.5 }}
                                    // className="notranslate"
                                  >
                                    <Chip
                                      label={`${
                                        building.type === "Tower"
                                          ? t("properties.floor")
                                          : t("properties.block")
                                      } ${unit.floor}`}
                                      size="small"
                                      sx={{
                                        fontSize: 14,
                                        borderRadius: 1.5,
                                        px: 1.2,
                                        py: 0.2,
                                      }}
                                    />
                                    <Chip
                                      label={`${
                                        building.type === "Tower"
                                          ? t("properties.flat")
                                          : t("properties.villa")
                                      } ${unit.flat_number}`}
                                      size="small"
                                      sx={{
                                        fontSize: 14,
                                        borderRadius: 1.5,
                                        px: 1.2,
                                        py: 0.2,
                                      }}
                                    />
                                  </Box>

                                  {unit.status === "RENTED" && unit.tenant && (
                                    <Box
                                      sx={{
                                        mt: 1,
                                        width: "100%",
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        sx={{
                                          color: "#90caf9",
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        {t("properties.tenant")}
                                      </Typography>
                                      <Chip
                                        className="notranslate"
                                        label={unit.tenant.fullname}
                                        size="small"
                                        variant="outlined"
                                        color="info"
                                        onClick={() =>
                                          handleShowTenant(unit.tenant)
                                        }
                                        sx={{
                                          cursor: "pointer",
                                          fontWeight: 600,
                                        }}
                                      />
                                    </Box>
                                  )}
                                  <Tooltip title={t("properties.edit_unit_name")}>
                                    <IconButton
                                      size="small"
                                      sx={{
                                        position: "absolute",
                                        bottom: 8,
                                        right: 40,
                                        color: theme.palette.primary.main,
                                      }}
                                      onClick={() => handleEditUnitName(unit)}
                                    >
                                      <Edit color="info" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t("properties.delete_unit")}>
                                    <IconButton
                                      size="small"
                                      sx={{
                                        position: "absolute",
                                        bottom: 8,
                                        right: 8,
                                        color: theme.palette.primary.main,
                                      }}
                                      onClick={() => {
                                        setCurrentUnit(unit);
                                        setDeleteDialogOpen(true);
                                      }}
                                    >
                                      <Delete color="error" />
                                    </IconButton>
                                  </Tooltip>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    ))
                  ) : (
                    <DataGrid
                      rows={filteredUnits.flatMap((building) =>
                        building.units.map((unit) => ({
                          id: unit.id, // required for DataGrid
                          unit,
                          building: building.building_name,
                          unitName: unit.name || `Flat ${unit.flat_number}`,
                          floor: unit.floor,
                          flatNumber: unit.flat_number,
                          type: building.type,
                          status: unit.status,
                          tenant: unit?.tenant,
                        }))
                      )}
                      columns={[
                        {
                          field: "building",
                          headerName: t("properties.col_building"),
                          flex: 1,
                          renderCell: (params) => (
                            <Typography className="notranslate">
                              {params?.value}
                            </Typography>
                          ),
                        },
                        {
                          field: "unitName",
                          headerName: t("properties.col_unit_name"),
                          flex: 3,
                          renderCell: (params) => {
                            const unit = params.row.unit;
                            return editingUnitId === unit.id ? (
                              <Box
                                className="notranslate"
                                display="flex"
                                alignItems="center"
                                justifyContent="left"
                                width="100%"
                                height="100%"
                              >
                                <Box display="flex" gap={1}>
                                  <TextField
                                    className="notranslate"
                                    size="small"
                                    value={editingUnitName}
                                    onChange={handleUnitNameChange}
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter")
                                        handleUnitNameSave(unit);
                                      if (e.key === "Escape")
                                        setEditingUnitId(null);
                                    }}
                                    sx={{ maxWidth: 170 }}
                                  />
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => handleUnitNameSave(unit)}
                                  >
                                    <Check />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setEditingUnitId(null)}
                                  >
                                    <Cancel />
                                  </IconButton>
                                </Box>
                              </Box>
                            ) : (
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="left"
                                width="100%"
                                height="100%"
                                className="notranslate"
                              >
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Tooltip title="Click to edit unit name">
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        cursor: "pointer",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        "&:hover": {
                                          textDecoration: "underline",
                                          color: theme.palette.primary.main,
                                        },
                                      }}
                                      onClick={() => handleEditUnitName(unit)}
                                    >
                                      {unit.name || `Flat ${unit.flat_number}`}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              </Box>
                            );
                          },
                        },
                        { field: "floor", headerName: t("properties.col_floor"), flex: 0.5 },
                        {
                          field: "flatNumber",
                          headerName: t("properties.col_flat_villa"),
                          flex: 0.5,
                        },
                        {
                          field: "type",
                          headerName: t("properties.col_type"),
                          flex: 0.5,
                          renderCell: (params) => (
                            <Typography >
                              {params?.value}
                            </Typography>
                          ),
                        },
                        {
                          field: "status",
                          headerName: t("properties.col_status"),
                          flex: 1,
                          renderCell: (params) =>
                            renderStatusChip(params.value),
                        },
                        {
                          field: "tenant",
                          headerName: t("properties.col_tenant"),
                          flex: 1,
                          renderCell: (params) =>
                            params.row.status === "RENTED" &&
                            params.row.tenant ? (
                              <Button
                                className="notranslate"
                                variant="outlined"
                                size="small"
                                onClick={() =>
                                  handleShowTenant(params.row.tenant)
                                }
                              >
                                {params.row.tenant.fullname}
                              </Button>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                —
                              </Typography>
                            ),
                        },
                        {
                          field: "actions",
                          headerName: t("properties.col_actions"),
                          flex: 1,
                          renderCell: (params) => (
                            <>
                              <Tooltip title="Edit UnitName">
                                <IconButton
                                  size="small"
                                  sx={{
                                    color: theme.palette.primary.main,
                                  }}
                                  onClick={() =>
                                    handleEditUnitName(params.row.unit)
                                  }
                                >
                                  <Edit color="info" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Unit">
                                <IconButton
                                  size="small"
                                  sx={{
                                    color: theme.palette.primary.main,
                                  }}
                                  onClick={() => {
                                    setCurrentUnit(params.row.unit);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Delete color="error" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ),
                        },
                      ]}
                      pageSize={10}
                      rowsPerPageOptions={[10, 25, 50]}
                      disableSelectionOnClick
                      sx={{
                        borderRadius: 2,
                        "& .MuiDataGrid-cell": {
                          display: "flex",
                          justifyContent: "left",
                          alignItems: "center",
                        },
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <AddModeDialog
        open={modeDialogOpen}
        onClose={() => setModeDialogOpen(false)}
        onSelectMode={handleAddMode}
      />
      <AddUnitsWizard
        open={addPropertyDlgOpen}
        onClose={() => {
          setAddPropertyDlgOpen(false);
          fetchInitData();
        }}
      />
      <AddSingleUnitDialog
        open={singleDialogOpen}
        onClose={() => setSingleDialogOpen(false)}
        onAdd={handleSingleAdd}
        structures={buildings}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteUnit}
        title={t("properties.confirm_deletion")}
        content={t("properties.confirm_delete_msg", { unitName: currentUnit?.name })}
        loading={isDeleting}
      />
    </Box>
  );
};

export default PropertyManagementPage;
